import groq from 'groq';

import type { SemboMarket } from '../../utils/markets';
import {
  contentDefinedByLocale,
  contentFilterByLocale,
  getImageQueryProperties,
} from './queryFragments';

export const getLanguageSuffixByMarket = (market: SemboMarket) =>
  getLanguageSuffix(market.lang);

export const getLanguageSuffix = (lang: string) =>
  lang.charAt(0).toUpperCase() + lang.slice(1);

export const getCopyQuery = (
  type: 'microcopy' | 'macrocopy',
  filters: { include?: string[]; exclude?: string[] } | undefined,
  market: SemboMarket
) => {
  const language = getLanguageSuffixByMarket(market);

  const includeFilter = filters?.include?.length
    ? `&& (${filters.include.map((item) => `key match "${item}"`).join(' || ')})`
    : '';
  const excludeFilter = filters?.exclude?.length
    ? `&& !(${filters.exclude.map((item) => `key match "${item}"`).join(' || ')})`
    : '';

  const baseValue = `coalesce(value${language}, valueEn)`;
  const value =
    type === 'microcopy'
      ? baseValue
      : `${baseValue}[] {
        ...,
        markDefs[]{
          ...,
          _type == "link" => {
            "content": ${contentFilterByLocale(market, { isReference: '@.reference->' })} {
              _type,
              slug
            },
            "username": @.reference->username
          }
        }
      }`;
  return groq`
    *[_type == '${type}' && ${baseValue} != null ${includeFilter} ${excludeFilter}] {
      key,
      "value": ${value}
    }
  `;
};

export const prefooterTags = (market: SemboMarket): string => {
  return groq`
  *[_type == 'tag' && ${contentDefinedByLocale(market)}] {
    _id,
    "count": count(*[_type == "trip" && references(^._id) && ${contentDefinedByLocale(market)}]),
    ...${contentFilterByLocale(market)} {
      "url": "/tag/" + slug.current,
      name
    }
  }  
  | order(name asc) [count > 0] {
    _id,
    url,
    name
  }
`;
};

export const slugDefinedInEntriesWithLanguage = (
  slug: string,
  excludedId: string,
  contentType: string,
  path: string
) => {
  const excludedPublishedId = excludedId.replace('drafts.', '');

  return groq`
    defined(
      *[
        _type == "${contentType}" && 
        !(_id in path("drafts.**")) &&
        _id != "${excludedPublishedId}" &&
        ${path}.current == "${slug}"
      ][0]
    )
  `;
};

export const mediaGroupById = (market: SemboMarket, id: string) => {
  return groq`
    *[_type == "mediaGroup" && _id == "${id}"]
    .images[] -> {
      ${getImageQueryProperties(market)}
    }
  `;
};

export const mediaFileById = (id: string) => {
  return groq`
    *[_type == "mediaFile" && _id == "${id}"][0].file.asset -> url
  `;
};

export const imageByRef = (market: SemboMarket, ref: string) => {
  return groq`*[_type == "media" && _id == "${ref}"]{
    ${getImageQueryProperties(market)}
  }[0]`;
};
